import { Extension } from '@tiptap/react';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import { canSplit, insertPoint } from '@tiptap/pm/transform';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import type { EditorView } from '@tiptap/pm/view';
import { apiUpload } from '../../lib/api';

const IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml']);

export interface ImageUploadOptions {
  onError?: (message: string) => void;
}

const key = new PluginKey('imageUploadPlaceholder');

interface PlaceholderMeta {
  add?: { id: symbol; pos: number; previewUrl: string };
  remove?: { id: symbol };
}

function getImageFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const files: File[] = [];
  for (let i = 0; i < dt.files.length; i++) {
    const f = dt.files[i];
    if (IMAGE_MIME_TYPES.has(f.type)) files.push(f);
  }
  return files;
}

/** Position of a placeholder by id, mapped through subsequent doc changes. */
function findPlaceholder(view: EditorView, id: symbol): number | null {
  const decos = key.getState(view.state) as DecorationSet | undefined;
  if (!decos) return null;
  const found = decos.find(undefined, undefined, (spec) => spec.id === id);
  return found.length ? found[0].from : null;
}

/**
 * Insert a block image at `pos` without leaving a blank line above it (#273).
 * A plain insert inside a paragraph splits it, so pasting on an empty line
 * left that line empty ABOVE the image. Instead the image goes before an empty
 * or line-start paragraph, after a line-end one, and splits the text mid-line.
 * A line follows the image so typing can continue below it. Returns the
 * position inside that line, or null if the fallback insert was used.
 */
function placeImage(tr: Transaction, pos: number, node: PMNode): number | null {
  const $pos = tr.doc.resolve(pos);
  // Pasted at the start of a line or mid-line: that text becomes the line after.
  let textFollows = $pos.parent.isTextblock && $pos.parentOffset === 0 && $pos.parent.content.size > 0;
  let at = insertPoint(tr.doc, pos, node.type);
  if (at == null && $pos.parent.isTextblock && canSplit(tr.doc, pos)) {
    tr.split(pos);
    at = pos + 1;
    textFollows = true;
  }
  if (at == null) {
    tr.replaceRangeWith(pos, pos, node);
    return null;
  }
  tr.insert(at, node);
  const end = at + node.nodeSize;
  if (textFollows) return end + 1;
  const $end = tr.doc.resolve(end);
  const next = $end.nodeAfter;
  if (!(next?.isTextblock && next.content.size === 0)) {
    const para = tr.doc.type.schema.nodes.paragraph;
    if (!para || !$end.parent.canReplaceWith($end.index(), $end.index(), para)) return null;
    tr.insert(end, para.create());
  }
  return end + 1;
}

function uploadFile(view: EditorView, file: File, insertPos: number, onError?: (m: string) => void) {
  const id = Symbol('img');
  const previewUrl = URL.createObjectURL(file);
  view.dispatch(view.state.tr.setMeta(key, { add: { id, pos: insertPos, previewUrl } } as PlaceholderMeta));

  const form = new FormData();
  form.append('file', file);
  apiUpload<{ url: string }>('/images', form)
    .then((result) => {
      const pos = findPlaceholder(view, id);
      const tr = view.state.tr.setMeta(key, { remove: { id } } as PlaceholderMeta);
      if (pos != null) {
        const node = view.state.schema.nodes.image.create({ src: result.url, alt: file.name });
        const sel = view.state.selection;
        const cursorHere = sel.empty && sel.from === pos;
        const lineAfter = placeImage(tr, pos, node);
        // Only move the cursor if the user hasn't moved it during the upload.
        if (cursorHere && lineAfter != null) tr.setSelection(TextSelection.create(tr.doc, lineAfter));
      }
      view.dispatch(tr);
    })
    .catch(() => {
      view.dispatch(view.state.tr.setMeta(key, { remove: { id } } as PlaceholderMeta));
      onError?.('Image upload failed');
    })
    .finally(() => URL.revokeObjectURL(previewUrl));
}

export const ImageUpload = Extension.create<ImageUploadOptions>({
  name: 'imageUpload',

  addOptions() {
    return { onError: undefined };
  },

  addProseMirrorPlugins() {
    const onError = this.options.onError;
    return [
      new Plugin({
        key,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            set = set.map(tr.mapping, tr.doc);
            const meta = tr.getMeta(key) as PlaceholderMeta | undefined;
            if (meta?.add) {
              const el = document.createElement('span');
              el.className = 'image-upload-placeholder';
              const img = document.createElement('img');
              img.src = meta.add.previewUrl;
              el.appendChild(img);
              const deco = Decoration.widget(meta.add.pos, el, { id: meta.add.id });
              set = set.add(tr.doc, [deco]);
            }
            if (meta?.remove) {
              set = set.remove(set.find(undefined, undefined, (spec) => spec.id === meta.remove!.id));
            }
            return set;
          },
        },
        props: {
          decorations(state) { return key.getState(state); },
          handleDrop(view, event, _slice, moved) {
            if (moved) return false;
            const files = getImageFiles((event as DragEvent).dataTransfer);
            if (!files.length) return false;
            event.preventDefault();
            const coords = view.posAtCoords({ left: (event as DragEvent).clientX, top: (event as DragEvent).clientY });
            const pos = coords?.pos ?? view.state.selection.from;
            files.forEach((f) => uploadFile(view, f, pos, onError));
            return true;
          },
          handlePaste(view, event) {
            const files = getImageFiles((event as ClipboardEvent).clipboardData);
            if (!files.length) return false;
            event.preventDefault();
            const pos = view.state.selection.from;
            files.forEach((f) => uploadFile(view, f, pos, onError));
            return true;
          },
        },
      }),
    ];
  },
});
